import { useState } from 'react';
import { Sparkles, X, Check, Image as ImageIcon } from 'lucide-react';
import { CLASS_3D_ICONS } from '../utils/classIcon';

interface ClassIconPickerProps {
  selectedIcon: string | null;
  onChange: (iconKey: string | null) => void;
}

export function ClassIconPicker({ selectedIcon, onChange }: ClassIconPickerProps) {
  const [isOpen, setIsOpen] = useState(false);

  const activeIcon = CLASS_3D_ICONS.find(i => i.id === selectedIcon);

  return (
    <div className="space-y-2">
      <label className="text-xs font-bold text-text-secondary flex items-center justify-between">
        <span className="flex items-center gap-1.5">
          <Sparkles size={14} className="text-accent-500" /> Sınıf 3D İkonu / Logosu
        </span>
        <span className="text-[11px] font-normal text-text-muted">Öğretmen Özel Seçimi</span>
      </label>

      {/* Seçili İkon Önizleme ve Butonlar */}
      <div className="p-3.5 rounded-2xl border border-border bg-surface flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          {activeIcon ? (
            <div className="w-12 h-12 rounded-xl bg-accent-500/10 p-1 flex items-center justify-center border border-accent-500/20 shadow-xs shrink-0">
              <img src={activeIcon.path} alt={activeIcon.name} className="w-full h-full object-contain filter drop-shadow-sm" />
            </div>
          ) : (
            <div className="w-12 h-12 rounded-xl bg-border/40 text-text-muted flex items-center justify-center font-bold text-xs shrink-0">
              <ImageIcon size={20} />
            </div>
          )}

          <div>
            <p className="font-bold text-xs text-text-primary">
              {activeIcon ? activeIcon.name : '3D İkon Seçilmedi'}
            </p>
            <p className="text-[11px] text-text-muted">
              {activeIcon ? 'Sınıf amblemi olarak bu ikon görünecektir.' : 'Sınıf logosunu özelleştirmek için bir 3D ikon seçin.'}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {activeIcon && (
            <button
              type="button"
              onClick={() => onChange(null)}
              className="p-2 rounded-xl text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-950/30 text-xs font-semibold transition-colors flex items-center gap-1"
              title="İkonu Kaldır"
            >
              <X size={14} /> Kaldır
            </button>
          )}

          <button
            type="button"
            onClick={() => setIsOpen(v => !v)}
            className="px-3 py-1.5 rounded-xl bg-accent-500/10 hover:bg-accent-500/20 text-accent-600 dark:text-accent-400 font-bold text-xs transition-colors"
          >
            {isOpen ? 'Kapat' : activeIcon ? 'Değiştir' : 'İkon Seç'}
          </button>
        </div>
      </div>

      {/* İkon Seçim Galerisi */}
      {isOpen && (
        <div className="p-4 rounded-2xl border border-accent-300 dark:border-accent-500/30 bg-accent-500/5 space-y-3 animate-fade-in">
          <div className="flex items-center justify-between border-b border-border pb-2">
            <p className="text-xs font-black uppercase tracking-wider text-text-secondary">
              Öğrenci & Okul 3D İkon Paketi
            </p>
            <button
              type="button"
              onClick={() => {
                onChange(null);
                setIsOpen(false);
              }}
              className="text-[11px] text-text-muted hover:text-text-primary underline"
            >
              İkon Kullanma
            </button>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2.5">
            {CLASS_3D_ICONS.map(icon => {
              const isSelected = selectedIcon === icon.id;

              return (
                <button
                  key={icon.id}
                  type="button"
                  onClick={() => {
                    onChange(icon.id);
                    setIsOpen(false);
                  }}
                  className={`p-3 rounded-2xl border flex flex-col items-center gap-2 transition-all hover:scale-105 active:scale-95 ${
                    isSelected
                      ? 'border-accent-500 bg-surface shadow-md ring-2 ring-accent-500/30'
                      : 'border-border bg-surface/70 hover:border-accent-300'
                  }`}
                >
                  <div className="w-10 h-10 flex items-center justify-center">
                    <img src={icon.path} alt={icon.name} className="w-full h-full object-contain filter drop-shadow-sm" />
                  </div>
                  <span className="text-[11px] font-bold text-text-primary text-center line-clamp-1">
                    {icon.name}
                  </span>
                  {isSelected && (
                    <span className="text-[10px] font-black uppercase text-accent-600 flex items-center gap-0.5">
                      <Check size={12} /> Seçildi
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
